import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

import { softwareDevelopmentData } from "../../../../data/SoftwareDevelopmentData";
import ServiceCard from "./ServiceCard";

// ============================================================
// ANIMATION VARIANTS
// ============================================================

const containerVariants = {
  hidden: {},

  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 25,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// ============================================================
// SERVICES GRID
// ============================================================

const ServicesGrid = () => {
  const { services } = softwareDevelopmentData;

  return (
    <section
      id="software-services"
      className="
        relative
        w-full
        overflow-hidden
        bg-[var(--bg-primary)]

        py-1.5

        min-[375px]:py-2

        sm:py-4
        md:py-6
        lg:py-10
      "
    >
      {/* =====================================================
          BACKGROUND GLOW - LEFT
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-24
          top-16
          h-40
          w-40
          rounded-full
          bg-orange-500/[0.05]
          blur-[85px]

          min-[375px]:-left-28
          min-[375px]:top-20
          min-[375px]:h-48
          min-[375px]:w-48
          min-[375px]:blur-[95px]

          sm:-left-32
          sm:top-24
          sm:h-72
          sm:w-72
          sm:blur-[130px]

          md:-left-40
          md:h-80
          md:w-80
        "
      />

      {/* =====================================================
          BACKGROUND GLOW - RIGHT
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -right-24
          bottom-6
          h-40
          w-40
          rounded-full
          bg-orange-500/[0.04]
          blur-[85px]

          min-[375px]:-right-28
          min-[375px]:bottom-8
          min-[375px]:h-48
          min-[375px]:w-48
          min-[375px]:blur-[95px]

          sm:-right-32
          sm:bottom-10
          sm:h-72
          sm:w-72
          sm:blur-[130px]

          md:-right-40
          md:h-80
          md:w-80
        "
      />

      {/* =====================================================
          TOP CENTER GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          left-1/2
          top-0
          h-28
          w-[220px]
          -translate-x-1/2
          rounded-full
          bg-orange-500/[0.025]
          blur-[70px]

          min-[375px]:h-32
          min-[375px]:w-[260px]
          min-[375px]:blur-[80px]

          sm:h-52
          sm:w-[420px]
          sm:blur-[110px]

          md:w-[500px]
        "
      />

      {/* =====================================================
          SUBTLE GRID
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          inset-0
          opacity-[0.018]

          [background-image:linear-gradient(var(--text-primary)_1px,transparent_1px),linear-gradient(90deg,var(--text-primary)_1px,transparent_1px)]

          [background-size:40px_40px]

          min-[375px]:[background-size:45px_45px]

          sm:[background-size:60px_60px]

          md:[background-size:70px_70px]
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          min-w-0

          px-3

          min-[375px]:px-4

          sm:px-5
          md:px-6
          lg:px-8
          xl:px-8
        "
      >
        {/* =================================================
            SECTION HEADER
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
            ease: "easeOut",
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          className="
            mx-auto
            mb-6
            w-full
            max-w-5xl
            min-w-0
            px-0.5
            text-center

            min-[375px]:mb-7

            sm:mb-10
            sm:px-0

            lg:mb-12
          "
        >
          {/* =================================================
              LABEL
          ================================================== */}

          <motion.span
            initial={{
              opacity: 0,
              y: 10,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.5,
            }}
            viewport={{
              once: true,
            }}
            className="
              inline-flex
              max-w-[95%]
              min-w-0
              items-center
              justify-center
              gap-1.5
              overflow-hidden
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/[0.08]

              px-2.5
              py-1.5

              text-center
              text-[8px]
              font-semibold
              uppercase
              tracking-[1.2px]
              text-orange-400

              min-[375px]:gap-2
              min-[375px]:px-3
              min-[375px]:text-[9px]
              min-[375px]:tracking-[1.6px]

              sm:px-4
              sm:py-2
              sm:text-xs
              sm:tracking-[2.5px]
            "
          >
            <span
              className="
                h-1.5
                w-1.5
                shrink-0
                rounded-full
                bg-orange-400
              "
            />

            <span className="truncate">
              Our Software Services
            </span>
          </motion.span>

          {/* =================================================
              HEADING
          ================================================== */}

          <motion.h2
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
              delay: 0.08,
              ease: "easeOut",
            }}
            viewport={{
              once: true,
            }}
            className="
              mx-auto
              mt-3
              max-w-full
              break-words

              text-[1.45rem]
              font-bold
              leading-[1.15]
              tracking-tight
              text-[var(--text-primary)]

              min-[375px]:mt-4
              min-[375px]:text-[1.6rem]

              sm:mt-5
              sm:text-3xl

              md:text-4xl

              lg:text-5xl
            "
          >
            Complete Software Solutions{" "}
            <span className="text-[var(--brand-primary)]">
              Under One Roof
            </span>
          </motion.h2>

          {/* =================================================
              DESCRIPTION
          ================================================== */}

          <motion.p
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
              delay: 0.16,
              ease: "easeOut",
            }}
            viewport={{
              once: true,
            }}
            className="
              mx-auto
              mt-2.5
              w-full
              max-w-full
              break-words

              text-[10.5px]
              leading-[1.65]
              text-[var(--text-secondary)]

              min-[375px]:mt-3
              min-[375px]:text-[11px]
              min-[375px]:leading-5

              sm:mt-4
              sm:max-w-3xl
              sm:text-sm
              sm:leading-6

              md:max-w-4xl
              md:text-base
              md:leading-7

              lg:max-w-5xl
            "
          >
            From custom business platforms to mobile applications,
            web solutions, CRM systems, and specialized software,
            we build scalable digital solutions designed around
            your goals, users, and long-term business growth.
          </motion.p>
        </motion.div>

        {/* =================================================
            SERVICES GRID
        ================================================== */}

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.08,
          }}
          variants={containerVariants}
          className="
            grid
            w-full
            min-w-0

            grid-cols-1

            gap-3

            min-[375px]:gap-3.5

            sm:grid-cols-2
            sm:gap-5

            lg:gap-6

            xl:grid-cols-3
          "
        >
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
            />
          ))}
        </motion.div>

        {/* =================================================
            EXPLORE ALL SERVICES
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 15,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.3,
          }}
          transition={{
            duration: 0.5,
            delay: 0.25,
          }}
          className="
            mt-4
            flex
            w-full
            justify-center

            min-[375px]:mt-5

            sm:mt-7
          "
        >
          <button
            type="button"
            aria-label="Explore all services"
            onClick={() => {
              window.scrollTo({
                top: document.body.scrollHeight,
                behavior: "smooth",
              });
            }}
            className="
              group
              inline-flex
              min-h-[28px]
              max-w-full
              items-center
              justify-center
              gap-1.5
              rounded-full
              px-2
              py-1

              text-[8px]
              font-medium
              text-[var(--text-primary)]
              opacity-35

              transition-colors
              duration-300

              hover:text-[var(--brand-primary)]
              hover:opacity-100

              focus:outline-none
              focus-visible:ring-1
              focus-visible:ring-orange-500/50

              min-[375px]:gap-2

              sm:min-h-[30px]
              sm:text-[9px]
            "
          >
            <span
              className="
                flex
                h-5
                w-5
                shrink-0
                items-center
                justify-center
                rounded-full
                border
                border-[var(--border-primary)]

                transition-all
                duration-300

                group-hover:border-orange-500/30
                group-hover:bg-[var(--brand-primary)]
                group-hover:text-black

                sm:h-6
                sm:w-6
              "
            >
              <ArrowUpRight
                size={10}
                className="
                  transition-transform
                  duration-300

                  group-hover:translate-x-0.5
                  group-hover:-translate-y-0.5

                  sm:h-3
                  sm:w-3
                "
              />
            </span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesGrid;